import { _notNil } from '@/littledash';
import { useCallback, useRef } from 'react';

let globalAbortController: AbortController | undefined;

const abortGlobal = (reason: string): void => {
  if (_notNil(globalAbortController)) {
    globalAbortController.abort(reason);
  }
};

const getGlobalSignal = (): AbortSignal => {
  abortGlobal('replaced');
  const abortController = new AbortController();
  globalAbortController = abortController;
  return abortController.signal;
};

interface UseGlobalEventListenerAbortOptions {
  onAbort: () => void;
}

export const useGlobalEventListenerAbort = ({ onAbort }: UseGlobalEventListenerAbortOptions) => {
  const currentSignalRef = useRef<AbortSignal | null>(null);
  const onAbortRef = useRef(onAbort);
  onAbortRef.current = onAbort;

  const handleAbortEvent = useCallback(() => {
    onAbortRef.current();
  }, []);

  const cleanupSignal = useCallback(
    (signal: AbortSignal): void => {
      signal.removeEventListener('abort', handleAbortEvent);
    },
    [handleAbortEvent]
  );

  const getSignal = useCallback((): AbortSignal => {
    if (_notNil(currentSignalRef.current)) {
      cleanupSignal(currentSignalRef.current);
    }

    const signal = getGlobalSignal();
    currentSignalRef.current = signal;
    signal.addEventListener('abort', handleAbortEvent);

    return signal;
  }, [handleAbortEvent, cleanupSignal]);

  const abort = useCallback(
    (reason: string): void => {
      abortGlobal(reason);
      if (_notNil(currentSignalRef.current)) {
        cleanupSignal(currentSignalRef.current);
        currentSignalRef.current = null;
      }
    },
    [cleanupSignal]
  );

  return { getSignal, abort };
};
